import cx from "clsx";
import { Comment } from "kreate-common/modules/business-types";
import ButtonTransparentV2 from "kreate-common/modules/kreate-ui/components/ButtonTransparentV2";
import DropdownMenu from "kreate-common/modules/kreate-ui/components/DropdownMenu";
import Flex from "kreate-common/modules/kreate-ui/components/Flex";
import { httpPost$DeleteComment } from "kreate-server/commands/DeleteComment/fetcher";
import { httpPost$LoveComment } from "kreate-server/commands/LoveComment/fetcher";
import { httpPost$UpdateComment } from "kreate-server/commands/UpdateComment/fetcher";
import moment from "moment";
import Link from "next/link";
import React from "react";
import {
  MdDelete,
  MdEdit,
  MdFavorite,
  MdFavoriteBorder,
  MdMoreHoriz,
} from "react-icons/md";

import { validateComment } from "../CommentComposer/utils";

import styles from "./index.module.scss";

import { useLoginStatus } from "@/modules/common-hooks/useLoginStatus";
import { useWithErrorHandler } from "@/modules/common-hooks/useWithErrorHandler";
import { BULLET } from "@/modules/common-utils/unicode";
import AvatarViewer from "@/modules/kreate-components/components/AvatarViewer";
import LongTextViewer from "@/modules/kreate-components/components/LongTextViewer";
import UserDisplayNameViewerV2 from "@/modules/kreate-components/components/UserDisplayNameViewerV2";
import { useModalTrigger$ModalAlert } from "@/modules/kreate-modals/modals/ModalAlert/hooks";
import { useModalTrigger$ModalConfirm } from "@/modules/kreate-modals/modals/ModalConfirm/hooks";
import { useModalTrigger$ModalFailure } from "@/modules/kreate-modals/modals/ModalFailure/hooks";
import { useModalTrigger$ModalPrompt } from "@/modules/kreate-modals/modals/ModalPrompt/hooks";
import { getLinkToPageProfile } from "@/modules/navigation/utils/getLinkTo";
import { useUser } from "@/modules/resources/hooks/useUser";

type Props = {
  className?: string;
  style?: React.CSSProperties;
  comment: Comment;
  revalidateEverything: () => Promise<void>;
};

export default function CommentViewer({
  className,
  style,
  comment,
  revalidateEverything,
}: Props) {
  const loginStatus = useLoginStatus();
  const [author] = useUser(comment.authorId);
  const isLoved =
    loginStatus?.loggedIn && comment.lovedBy.includes(loginStatus.userId);

  const modalTriggers = {
    ModalPrompt: useModalTrigger$ModalPrompt(),
    ModalConfirm: useModalTrigger$ModalConfirm(),
    ModalAlert: useModalTrigger$ModalAlert(),
    ModalFailure: useModalTrigger$ModalFailure(),
  };

  const { busy, withErrorHandler } = useWithErrorHandler((error) =>
    modalTriggers.ModalFailure.openModalWithError(error)
  );

  const handleUpdateComment = async (comment: Comment) =>
    await withErrorHandler(async () => {
      const newCommentContent = await modalTriggers.ModalPrompt.openModal(
        "Change comment to:",
        {
          initialValue: comment.content,
          inputProps: { multiline: 3 },
          validate: validateComment,
        }
      );
      await httpPost$UpdateComment(
        `/api/v1/comment/${comment.commentId}/edit`,
        { content: newCommentContent }
      );
      await revalidateEverything();
    })();

  const handleDeleteComment = async (comment: Comment) => {
    await withErrorHandler(async () => {
      const ok = await modalTriggers.ModalConfirm.openModal(
        "Do you want to remove this comment?"
      );
      if (!ok) return;
      await httpPost$DeleteComment(
        `/api/v1/comment/${comment.commentId}/delete`,
        {}
      );
      await revalidateEverything();
    })();
  };

  return (
    <div className={cx(styles.container, className)} style={style}>
      <Flex.Row gap="16px" padding="24px 0">
        <Flex.Row flex="0 0 auto">
          <Link href={author ? getLinkToPageProfile(author) : "#disabled"}>
            <AvatarViewer size="40px" value={author?.avatar} />
          </Link>
        </Flex.Row>
        <Flex.Col flex="1 1 128px" gap="8px">
          <Flex.Row alignItems="center" gap="8px">
            <Flex.Cell flex="0 1 auto" minWidth="0" asChild>
              <UserDisplayNameViewerV2
                className={styles.authorName}
                user={author}
                unstyled
              />
            </Flex.Cell>
            <Flex.Cell className={styles.bullet} flex="0 0 auto">
              {BULLET}
            </Flex.Cell>
            <Flex.Cell className={styles.createdAt} flex="0 0 auto">
              <div title={moment(comment.createdAt).format()}>
                {moment(comment.createdAt).fromNow()}
              </div>
            </Flex.Cell>
            <Flex.Cell flex="1 1 0" minWidth="0"></Flex.Cell>
            {loginStatus?.loggedIn &&
            loginStatus.userId === comment.authorId ? (
              <Flex.Row>
                <DropdownMenu.Menu
                  trigger={
                    <ButtonTransparentV2 disabled={busy} loading={busy}>
                      <MdMoreHoriz size="24px" style={{ margin: "-4px" }} />
                    </ButtonTransparentV2>
                  }
                >
                  <DropdownMenu.Item
                    icon={<MdEdit size="24px" />}
                    content="Edit"
                    onSelect={async () => await handleUpdateComment(comment)}
                  />
                  <DropdownMenu.Item
                    icon={<MdDelete size="24px" />}
                    content="Remove"
                    onSelect={async () => await handleDeleteComment(comment)}
                  />
                </DropdownMenu.Menu>
              </Flex.Row>
            ) : null}
          </Flex.Row>
          <Flex.Cell>
            <LongTextViewer.Collapsible
              className={styles.comment}
              value={comment.content}
              unstyled
            />
          </Flex.Cell>
          <Flex.Row gap="4px" alignItems="center" paddingTop="4px">
            <ButtonTransparentV2
              content={
                isLoved ? (
                  <MdFavorite
                    size="20px"
                    className={styles.colorPrimary}
                    style={{ margin: "-1px" }}
                  />
                ) : (
                  <MdFavoriteBorder
                    size="20px"
                    className={styles.colorBlack}
                    style={{ margin: "-1px" }}
                  />
                )
              }
              onClick={async () => {
                if (!loginStatus?.loggedIn) {
                  await modalTriggers.ModalAlert.openModal(
                    "You have to login to perform this action."
                  );
                  return;
                }
                await httpPost$LoveComment(
                  `/api/v1/comment/${comment.commentId}/love`,
                  { love: !isLoved }
                );
                await revalidateEverything();
              }}
            />
            <span
              className={cx(
                styles.loveCount,
                isLoved ? styles.colorPrimary : styles.colorBlack
              )}
            >
              {comment.lovedBy.length}
            </span>
          </Flex.Row>
        </Flex.Col>
      </Flex.Row>
    </div>
  );
}
